import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { clamp } from '@orderna/admin-panel/src/utils/clamp';
import {
  ButtonComponent,
  ButtonTextDirective,
} from '../button/button.component';
import { InputSelectComponent } from '../input/select/select.component';
import { MatSelectModule } from '@angular/material/select';

@Component({
  selector: 'app-pagination',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    ButtonTextDirective,
    MatSelectModule,
  ],
  templateUrl: './pagination.component.html',
  styleUrls: [
    '/src/app/shared/components/input/select/select.component.scss',
    './pagination.component.css',
  ],
})
export class PaginationComponent {
  @Input() numberOfItems!: number;
  @Input() currentPage!: number;

  /**
   * Defines a fixed number of items per page for pagination.
   * This value remains constant and does not change dynamically.
   */
  @Input() perPage: number = 10;

  /**
   * Provides a list of selectable options for the number of items per page.
   * Allows users to dynamically adjust the pagination size.
   * Example: [10, 100, 1000]
   */
  @Input() perPageOptions: number[] = [];

  @Input() changeRouteQuery: boolean = true;

  @Output() pageChange: EventEmitter<{ currentPage: number }> =
    new EventEmitter();
  @Output() perPageChange: EventEmitter<{ perPage: number }> =
    new EventEmitter();

  route: ActivatedRoute = inject(ActivatedRoute);
  router: Router = inject(Router);
  cdr = inject(ChangeDetectorRef);

  get numberOfPages() {
    const dividend = this.numberOfItems === 0 ? 1 : this.numberOfItems;
    return Math.ceil(dividend / this.perPage);
  }

  get isFirstPage() {
    return this.currentPage === 1;
  }

  get isLastPage() {
    return this.currentPage === this.numberOfPages;
  }

  changePage(newPage: number) {
    if (this.changeRouteQuery) {
      this.router.navigate(['.'], {
        relativeTo: this.route,
        queryParams: {
          ...this.route.snapshot.queryParams,
          page: clamp(newPage, 1, this.numberOfPages),
        },
        queryParamsHandling: 'merge',
      });
    } else {
      this.pageChange.emit({ currentPage: newPage });
    }
  }

  changePerPage(perPage: number) {
    this.perPage = perPage;
    this.perPageChange.emit({ perPage: this.perPage});

    if (!this.changeRouteQuery) return;
    this.router.navigate(['.'], {
      relativeTo: this.route,
      queryParams: {
        size: this.perPage,
      },
      queryParamsHandling: 'merge',
    });
  }
}
